import { BiLocalizedText } from './catalog';
import type { BiDisplayFormat } from './format';
import { BiDateWindow } from './query';

// Mirror of backend `models/src/bi/result.ts`.

export enum BiFieldType {
  String = 'string',
  Number = 'number',
  Date = 'date',
  Datetime = 'datetime',
  Boolean = 'boolean',
}

export enum BiFieldRole {
  Dimension = 'dimension',
  Measure = 'measure',
}

/**
 * How a coded dimension's values read: the stored code (as text, so a
 * numeric level `80` is `'80'`) → its Chinese label. Values stay codes
 * wherever they travel or are stored — rows, filters, specs, drafts,
 * exports; a code without a label shows as itself.
 */
export type BiValueLabels = Record<string, string>;

/** A cell as its column shows it: a coded value's label, else unchanged. */
export function biLabelledCell(
  cell: BiCell,
  valueLabels: BiValueLabels | undefined,
): BiCell {
  if (!valueLabels || cell === null || typeof cell === 'boolean') return cell;
  return valueLabels[String(cell)] ?? cell;
}

export interface BiResultColumn {
  key: string;
  label: BiLocalizedText;
  type: BiFieldType;
  role: BiFieldRole;
  /**
   * A measure's format as its dataset resolves it: the ratio's own, else
   * the measure's default, else the type's. A viz may still override it
   * ({@link biColumnFormat}).
   */
  format?: BiDisplayFormat;
  /**
   * A coded dimension's display labels, from its dataset. The cells keep
   * the raw code; only what is shown reads the label.
   */
  valueLabels?: BiValueLabels;
  /**
   * The blend query a measure comes from, 0-based, for the chart's side
   * legend. Absent on a single query's columns and on a blend's merge keys.
   */
  side?: number;
}

export type BiCell = string | number | boolean | null;

export interface BiQueryResult {
  columns: BiResultColumn[];
  /** One array per row, in `columns` order. */
  rows: BiCell[][];
  /** More rows matched than the limit returned. */
  truncated: boolean;
  /** ISO time the data was read. */
  asOf: string;
  /**
   * The dates the time range resolved to, for a caption. Absent without a
   * range, and on a blend whose sides resolved to different dates.
   */
  window?: BiDateWindow;
}
